import React, { useState, useEffect } from 'react';
import SingleSelectDropdown from './SingleSelectDropdown/SingleSelectDropdown';
import SingleSelectRadios from './SingleSelectRadios/SingleSelectRadios';
import MultiSelectCheckboxes from './MultiSelectCheckboxes/MultiSelectCheckboxes';
import ValidationError from '../ValidationError/ValidationError';
import { SelectOption, CheckedOption } from '../types';
import { OTHER_OPTION_ID } from '../constants';
import styles from './SelectQuestion.scss';
import Submission from '../Submission/Submission';
import notifySavingError from 'ca-capture/helpers/notifySavingError';
import request from 'ca-request';
import { SelectQuestionContainer } from 'ca-capture/SelectQuestion/SelectQuestionContainer';
import { InputContainer } from 'ca-capture/SelectQuestion/InputContainer';

export type Props = {
  questionId: string;
  questionText: string;
  questionMandatory: boolean;
  selectType: 'single' | 'multi';
  options: SelectOption[];
  selectedOptionIds: string[];
  existingOtherValue: string;
  selectionLimit: number;
  requestURL: string;
  isPDF: boolean;
  uiText: {
    selectCount: string;
    selectionLimitError: string;
    savingError: string;
    mandatoryError: string;
    search: string;
    noMatchesFound: string;
    optionsAvailable: string;
    otherLabel: string;
    forLabel: string;
  };
  isLTR: boolean;
  radioThreshold: number;
};

const SelectQuestion = (props: Props): React$Element<string> => {
  const {
    questionId,
    questionText,
    questionMandatory,
    selectType,
    options,
    selectedOptionIds,
    existingOtherValue,
    selectionLimit,
    requestURL,
    uiText,
    isPDF,
    isLTR,
    radioThreshold,
  } = props;
  const questionHeaderId = `question-header-${questionId}`;
  const questionLimitId = `question-limit-${questionId}`;
  const questionLabel = questionMandatory ? `${questionText} *` : questionText;
  const containerRef = React.createRef<HTMLDivElement>();
  const singularSelectedOption = selectedOptionIds.length
    ? selectedOptionIds[0]
    : null;
  const hasOtherOption =
    options.find((o) => o.value === OTHER_OPTION_ID) !== undefined;
  const showRadiosOverDropdown =
    options.length <= radioThreshold || isPDF || hasOtherOption;
  const showDropdown = selectType === 'single' && !showRadiosOverDropdown;
  const validationErrorId = `${questionId}-error`;
  const selectInputId = `select-dropdown-input-${questionId}`;

  const [validationError, setValidationError] = useState<boolean>(false);

  // Without having a React container around the Capture form yet, we're listening for a custom event
  // sent from app/assets/javascripts/response.js to signal that submit has been pressed and we want to show validation errors.
  useEffect(() => {
    const checkAndSetError = (_event: any, questionIds: string[]) => {
      const answered = questionIds.indexOf(questionId) === -1;
      setValidationError(!answered);
    };
    if (questionMandatory) {
      $(document).on('promptUnanswered', checkAndSetError);
    }
    return () => {
      $(document).off('promptUnanswered', checkAndSetError);
    };
  }, [questionId, questionMandatory]);

  const onSave = (
    optionsChecked: CheckedOption[],
    otherValue: string,
    checkedOption: string,
    answered: boolean
  ) => {
    if (answered) {
      setValidationError(false);
    }

    const optionObject = Object.assign(
      {},
      ...optionsChecked.map((o) => ({ [o.id]: o.checked }))
    );
    const data = {
      ...optionObject,
      text_value: otherValue,
      clicked_option: checkedOption,
    };

    Submission.enqueue(() => {
      return request
        .put(requestURL, {
          body: JSON.stringify(data),
        })
        .catch((error) => {
          notifySavingError(
            error,
            requestURL,
            'select request',
            uiText.savingError
          );
        });
    });
  };

  const renderInputs = (): React$Element<string> => {
    if (selectType === 'multi') {
      return (
        <MultiSelectCheckboxes
          questionId={questionId}
          questionHeaderId={questionHeaderId}
          questionText={questionText}
          options={options}
          existingOtherValue={existingOtherValue}
          existingSelections={selectedOptionIds}
          selectionLimit={selectionLimit}
          questionMandatory={questionMandatory}
          onSave={onSave}
          containerRef={containerRef}
          uiText={uiText}
          isPDF={isPDF}
          validationError={validationError}
          validationErrorId={validationErrorId}
          {...(selectionLimit > 0 && { questionLimitId })}
        />
      );
    }

    if (showRadiosOverDropdown) {
      return (
        <SingleSelectRadios
          questionHeaderId={questionHeaderId}
          questionId={questionId}
          questionText={questionText}
          options={options}
          existingSelection={singularSelectedOption}
          existingOtherValue={existingOtherValue}
          onSave={onSave}
          containerRef={containerRef}
          isPDF={isPDF}
          validationError={validationError}
          validationErrorId={validationErrorId}
          uiText={uiText}
          radioThreshold={radioThreshold}
          questionMandatory={questionMandatory}
        />
      );
    }

    return (
      <SingleSelectDropdown
        options={options}
        currentSelection={singularSelectedOption}
        inputId={selectInputId}
        ariaLabelledBy={questionHeaderId}
        questionId={questionId}
        validationError={validationError ? validationErrorId : undefined}
        containerRef={containerRef}
        onSave={onSave}
      />
    );
  };

  const renderQuestionText = () => {
    if (showDropdown) {
      return (
        <label
          htmlFor={selectInputId}
          className={styles.questionHeader}
          id={questionHeaderId}
          tabIndex={-1}
        >
          {questionLabel}
        </label>
      );
    }

    return (
      <div
        className={styles.questionHeader}
        id={questionHeaderId}
        tabIndex={-1}
      >
        {questionLabel}
      </div>
    );
  };

  return (
    <SelectQuestionContainer ref={containerRef}>
      {renderQuestionText()}
      <div>
        {selectType === 'multi' && selectionLimit > 0 && (
          <div id={questionLimitId} className={styles.limitInfo}>
            {uiText.selectCount}
          </div>
        )}
      </div>
      <InputContainer>{renderInputs()}</InputContainer>
      {validationError && (
        <ValidationError
          id={validationErrorId}
          uiText={{ message: uiText.mandatoryError }}
        />
      )}
    </SelectQuestionContainer>
  );
};

export default SelectQuestion;
